// Fase 27A: cliente HTTP MINIMO del signup autoservicio (publico) + onboarding (tenant). No dispara el
// refresh tenant. ONB-001 añade continuar (correo + contraseña) y preparar el negocio (Business + primera
// tienda + sesión del dueño en cookies). ONB-002: entre pasos manda la sesión corta de registro, una cookie
// httpOnly que pone el servidor (este código nunca la ve); ningún secreto viaja en los cuerpos.
import type { AuthBusiness, AuthStore, AuthUser } from '../types/auth';

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3000').replace(/\/$/, '');

export class SelfServiceApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly code?: string) {
    super(message);
    this.name = 'SelfServiceApiError';
  }
}

async function req<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, { ...options, credentials: 'include', headers });
  } catch {
    throw new SelfServiceApiError(0, 'No fue posible conectar con el servidor.');
  }
  const text = await res.text();
  let payload: unknown;
  try { payload = text ? JSON.parse(text) : undefined; } catch { payload = undefined; }
  if (!res.ok) {
    const p = (payload ?? {}) as { message?: string; error?: string };
    throw new SelfServiceApiError(res.status, p.message || 'Ocurrió un error.', p.error);
  }
  return payload as T;
}

// --- Configuracion publica (Fase 27B) ---
// Flags booleanas seguras: si el frontend debe ofrecer el registro autoservicio. Sin secretos.
/** P-085: el contacto PÚBLICO de ayuda (un correo y/o un WhatsApp de soporte); null = no configurado. */
export interface SupportContact { email: string | null; whatsapp: string | null }
export interface PublicConfig { signupEnabled: boolean; emailDeliveryAvailable: boolean; support?: SupportContact }
export const getPublicConfig = () => req<PublicConfig>('/api/public/config');

// --- Signup publico ---
// ONB-002: el registro es nombre + correo + la contraseña de la cuenta (la del dueño). El servidor deja la
// sesión corta de registro en una cookie httpOnly.
export interface StartSignupBody { displayName: string; email: string; password: string }
export const startSignup = (body: StartSignupBody) => req<{ message: string }>('/api/signup', { method: 'POST', body: JSON.stringify(body) });
export const resendVerification = (email: string) => req<{ message: string }>('/api/signup/resend', { method: 'POST', body: JSON.stringify({ email }) });

export type SignupStatus = 'PENDING_EMAIL_VERIFICATION' | 'EMAIL_VERIFIED' | 'PROVISIONED';
// `registrationId`: la solicitud concreta; preparar el negocio (y sus reintentos) va contra ella.
export interface SignupView { registrationId: string; status: SignupStatus; email: string; displayName: string; businessName?: string | null }
// ONB-003: `needsPassword`: este navegador abrió el enlace sin haber iniciado el registro; sigue con la
// contraseña de la cuenta.
export interface SessionView extends SignupView { needsPassword: boolean }
// `canContinue`: este navegador ya tiene la sesión verificada de ESA solicitud y puede preparar el negocio.
export interface VerifyResult extends SessionView { canContinue: boolean }
// ONB-002/ONB-003: abrir el enlace verifica el correo sin pedir nada; el servidor le da a este navegador la
// única sesión de registro que sirve después (cookie). No abre la sesión del dueño ni crea el negocio.
export const verifySignup = (token: string) => req<VerifyResult>('/api/signup/verify', { method: 'POST', body: JSON.stringify({ token }) });
// Correo + la contraseña de la cuenta. Antes de verificar retoma la solicitud; después, solo en el
// navegador que abrió el enlace. Si no, 409 SIGNUP_LINK_REQUIRED (y se envía un enlace nuevo al correo).
export const continueSignup = (email: string, password: string) =>
  req<SignupView>('/api/signup/continue', { method: 'POST', body: JSON.stringify({ email, password }) });
// En qué punto está la solicitud de la sesión corta de este navegador (401 si no hay).
export const getSignupSession = () => req<SessionView>('/api/signup/session');

export interface SetupBusinessBody { registrationId: string; businessName: string; countryCode: string; currencyCode: string; storeName: string; timezone: string }
export interface SetupBusinessResult { status: 'PROVISIONED'; business: AuthBusiness; user: AuthUser; stores: AuthStore[] }
// Crea el negocio y su primera tienda y deja abierta la sesión del dueño (cookies httpOnly).
export const setupBusiness = (body: SetupBusinessBody) => req<SetupBusinessResult>('/api/signup/setup', { method: 'POST', body: JSON.stringify(body) });

// --- ONB-010: invitación a la beta privada (pública solo con su enlace; 404 si está apagada) ---
// La misma sesión corta de registro (cookie httpOnly) que el registro guiado; el token viaja en el cuerpo (no en la
// URL de la API) y nunca vuelve en una respuesta.
export type InviteStatus = 'PENDING' | 'ACCEPTED' | 'PROVISIONED';
export interface InviteLinkView { status: InviteStatus; email: string; displayName: string | null; canContinue: boolean }
export const inspectInvite = (token: string) => req<InviteLinkView>('/api/beta-invites/inspect', { method: 'POST', body: JSON.stringify({ token }) });
export const acceptInvite = (body: { token: string; displayName: string; password: string }) =>
  req<SignupView>('/api/beta-invites/accept', { method: 'POST', body: JSON.stringify(body) });
export const claimInvite = (token: string, password: string) => req<SignupView>('/api/beta-invites/claim', { method: 'POST', body: JSON.stringify({ token, password }) });
export const getInviteSession = () => req<SessionView>('/api/beta-invites/session');
export const setupInviteBusiness = (body: SetupBusinessBody) => req<SetupBusinessResult>('/api/beta-invites/setup', { method: 'POST', body: JSON.stringify(body) });

// --- Onboarding tenant ---
export interface OnboardingStatus {
  status: 'IN_PROGRESS' | 'COMPLETED';
  version: number;
  currentVersion: number;
  completedSteps: string[];
  startedAt: string;
  completedAt: string | null;
  data: { activeStores: number };
  nextAction: string | null;
}
export const getOnboarding = () => req<OnboardingStatus>('/api/onboarding');
export interface FirstStoreBody { name: string; timezone: string; city?: string; region?: string; countryCode?: string }
export const configureFirstStore = (body: FirstStoreBody) => req<OnboardingStatus>('/api/onboarding/first-store', { method: 'POST', body: JSON.stringify(body) });
export const completeOnboarding = () => req<OnboardingStatus>('/api/onboarding/complete', { method: 'POST', body: JSON.stringify({}) });
